import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import { encodeDate, unpackToTime } from '@campfire/hot-date';
import { Box, List, Typography } from '@material-ui/core';
import { ArrayParam, useQueryParams, StringParam } from 'use-query-params';
import { MyElementsMyActivityType } from './my-elements-my-activity-types';
import { MyElementsMyActivityListItem } from './MyElementsMyActivityListItem';
import { ShowMoreButton } from '../../../../../../common/buttons/ShowMoreButton';
import { AlertCard, getAlertCardColors } from '../../../../../../common/cards/alert-card/AlertCard';
import { useSharedActivitiesFilterUpdate } from '../my-activities-filters/MyActivitiesFilters';
import { GetVolDashboardMyElementsRosterItemStatusData as VolunteerData } from '../../__generated__/GetVolDashboardMyElementsRosterItemStatusData';
import { useCampfireTheme } from '../../../../../../theme/useCampfireTheme';
import { MyActivityStatusIndicator } from './MyActivityStatusIndicator';
import { useActivityActions } from '../../../../activities-v2/useActivityActions';
import { getRosterActions } from '../../../../activities-v2/helpers';
import { ActivityAvailability } from '../../../../../../common/availability-center/ActivityAvailability/ActivityAvailability';
import { AlertFullSessionDialog } from '../../../../../../common/alert-full-session-dialog/AlertFullSessionDialog';
import { RosterTimelineItem } from '../../../../activities-v2/ActivityTypes';
import { MyActivityCICOActions } from './CICO/MyActivityCICOActions';
import { MyActivityCheckInDialog } from './CICO/MyActivityCheckInDialog';
import { useUser } from '../../../../../../global/auth/useUser';
import { MyActivityCheckOutDialog } from './CICO/MyActivityCheckOutDialog';
import { CheckOutFinishedNext } from './CICO/CheckOutFinishedNext';
import { LeavePeriodCard } from '../../../../../../common/cards/leave-period-card/LeavePeriodCard';
import { useSharedAvailabilityCentreDialogState } from '../../../../../../global/components/AvailabilityCenter/AvailabilityCenter';
import { useViewUpcomingRoster } from '../../../../../../global/onboarding/use-view-upcoming-rosters';
import { useOnboardingContext } from '../../../../../../global/onboarding/useOnboardingContext';
import { useOrgInfo } from '../../../../../../global/auth/use-org-info/use-org-info';

export const SHOWACTIVITYINITSIZE = 6;
interface MyElementsMyActivitiesListProps {
  myActivities: RosterTimelineItem[];
  volunteerData?: VolunteerData;
  refetch?: () => void;
  refetchListRosters?: () => void;
  onLoadMore: () => void;
  isLoading: boolean;
}

export const MyElementsMyActivitiesList = (props: MyElementsMyActivitiesListProps) => {
  const [showSize, setShowSize] = useState<number>(SHOWACTIVITYINITSIZE);
  const [checkOutFinishedSessionId, setCheckOutFinishedSessionId] = useState<string>();
  const { myActivities, volunteerData, refetch, refetchListRosters, onLoadMore, isLoading } = props;
  const { darkTextColor, lightTextColor } = getAlertCardColors('info');
  const { isMobile } = useCampfireTheme();
  const org = useOrgInfo();
  const rosterWord = org?.menuLabels?.wordRoster?.toLowerCase() || 'roster';
  const activityWord = org?.menuLabels?.wordActivity?.toLowerCase() || 'activity';
  const activityArticle = /^[aeiou]/i.test(activityWord) ? 'an' : 'a';
  const activityWordPlural = /[^aeiou]y$/i.test(activityWord) ? `${activityWord.slice(0, -1)}ies` : `${activityWord}s`;
  const { activitiesFilterUpdated, setActivitiesFilterUpdated } = useSharedActivitiesFilterUpdate();
  const {
    setDialogOpen,
    setInitialOperation,
    setAdditionalRefetchFunctions,
  } = useSharedAvailabilityCentreDialogState();

  // Set up refetch functions for the AvailabilityCenter
  React.useEffect(() => {
    const refetchFunctions = [];
    if (refetch) refetchFunctions.push(refetch);
    if (refetchListRosters) refetchFunctions.push(refetchListRosters);
    setAdditionalRefetchFunctions(refetchFunctions);
  }, [refetch, refetchListRosters, setAdditionalRefetchFunctions]);
  const handleSizeUpdate = () => {
    const newSize = showSize + SHOWACTIVITYINITSIZE;
    setShowSize(newSize);
    if (newSize > myActivities.length) {
      onLoadMore();
    }
  };
  const [query, setQuery] = useQueryParams({
    activityStatus: ArrayParam,
    activityId: StringParam,
    activityDate: StringParam,
  });

  const { getVolunteerIdentity, user } = useUser();
  const { volunteerId } = getVolunteerIdentity();
  const { userIdentityService, configItems } = user;

  if (activitiesFilterUpdated) {
    handleSizeUpdate();
    setActivitiesFilterUpdated(false);
  }

  const displayingActivity = (activity: RosterTimelineItem): MyElementsMyActivityType => ({
    activityId: activity.activityId,
    activityName: activity.name,
    startTime: activity.startTime,
    endTime: activity.endTime,
    hasOpenRoster: activity.hasOpenRoster,
    numSessions: activity.sessions.length,
    activityDate: activity.activityDate,
    activityLocation: activity?.rosterTemplateAddress
      ? activity?.rosterTemplateAddress
      : activity.activityLocation
      ? activity.activityLocation.placesAddress.description
      : activity.activityRemoteLocation?.details
      ? activity.activityRemoteLocation.details
      : (activity as any).effectiveAddressRevealBeforeDays != null ||
        (activity as any).effectiveAddressHideAfterDays != null
      ? (() => {
          if ((activity as any).effectiveAddressRevealBeforeDays != null && activity.activityDate) {
            const rosterDate = new Date(`${activity.activityDate}T00:00:00`);
            const revealDate = new Date(rosterDate);
            revealDate.setDate(revealDate.getDate() - (activity as any).effectiveAddressRevealBeforeDays);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const daysUntil = Math.ceil((revealDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
            if (daysUntil > 0) {
              return `Address available in ${daysUntil} day${daysUntil === 1 ? '' : 's'}`;
            }
          }
          return 'Address not available';
        })()
      : undefined,
    programName: activity.program.name,
    sessions: activity.sessions,
  });

  const { theme } = useCampfireTheme();

  const getPublishedRostering = (activity: RosterTimelineItem) =>
    volunteerData?.vm.volunteer?.rosterings.find(
      (rostering) =>
        rostering.publishedRoster.activityDate === activity.activityDate &&
        rostering.publishedRoster.activity.activityId === activity.activityId
    );

  const filteredActivities = myActivities
    .filter((myActivity) => !query.activityStatus || query.activityStatus.includes(myActivity.status))
    .filter((myActivity) => {
      // If see-rostered-dates-only is enabled and user is a volunteer,
      // only show rostered dates
      if (userIdentityService.privLevel === 'volunteer' && configItems.vm.seeRosteredDatesOnly === true) {
        return myActivity.status === 'rostered' || myActivity.status === 'attended';
      }
      return true;
    });

  // Get volunteer unavailabilities and create a list with both activities and leave cards
  const volunteerUnavailabilities = volunteerData?.vm.volunteer?.unavailabilities || [];

  // Build a combined list of activities and leave period indicators
  const activitiesWithLeaveCards = React.useMemo(() => {
    const items: Array<{ type: 'activity' | 'leave'; data: any }> = [];

    // Add activities to the list
    filteredActivities.forEach((activity) => {
      items.push({ type: 'activity', data: activity });
    });

    // Add leave period start/end cards
    volunteerUnavailabilities.forEach((leave) => {
      // Add start card
      items.push({
        type: 'leave',
        data: {
          id: `leave-start-${leave.volunteerUnavailabilityId}`,
          date: leave.startDate,
          endDate: leave.endDate,
          leaveType: 'start',
        },
      });

      // Add end card (only if end date differs from start date)
      if (leave.startDate !== leave.endDate) {
        items.push({
          type: 'leave',
          data: {
            id: `leave-end-${leave.volunteerUnavailabilityId}`,
            date: leave.endDate,
            leaveType: 'end',
          },
        });
      }
    });

    // Sort by date, with leave periods appearing before activities on the same date
    items.sort((a, b) => {
      const dateA = a.type === 'activity' ? a.data.activityDate : a.data.date;
      const dateB = b.type === 'activity' ? b.data.activityDate : b.data.date;
      const dateComparison = DateTime.fromISO(dateA).toMillis() - DateTime.fromISO(dateB).toMillis();

      // If dates are the same, prioritize leave periods (type 'leave') over activities
      if (dateComparison === 0) {
        if (a.type === 'leave' && b.type === 'activity') return -1;
        if (a.type === 'activity' && b.type === 'leave') return 1;
      }

      return dateComparison;
    });

    return items;
  }, [filteredActivities, volunteerUnavailabilities]);

  const handleEditLeave = () => {
    setInitialOperation('unavailability');
    setDialogOpen(true);
  };

  const {
    onAction,
    showDialog,
    showAlertDialog,
    onCloseDialog,
    selectedActivity,
    onSubmit,
    showUpdateDialog,
    fullSessionIds,
    activityIdSubmited,
    handleAlertClose,
    activityDatesSubmited,
    onCICOAction,
    showCheckInDialog,
    showCheckOutDialog,
    CICODialogType,
    setShowCheckOutDialog,
  } = useActivityActions(refetch);

  const onActivityAction = (action: any, activity: RosterTimelineItem) => {
    const enrolmentId = volunteerData?.vm.volunteer?.activityEnrolments.find(
      (enrolment) => !enrolment.dateRemoved && enrolment.activity.activityId === activity.activityId
    )?.activityEnrolmentId;
    const publishedRosteringId = volunteerData?.vm.volunteer?.rosterings.find(
      (rostering) =>
        !rostering.dateRemoved &&
        rostering.publishedRoster.activityDate === activity.activityDate &&
        rostering.publishedRoster.activity.activityId === activity.activityId
    )?.publishedRosteringId;
    const activityApplicationId = volunteerData?.vm.volunteer?.activityApplications.find(
      (application) => !application.dateWithdrawn && application.activity.activityId === activity.activityId
    )?.activityApplicationId;
    const activityWaitlistingId = volunteerData?.vm.volunteer?.activityWaitlistings.find(
      (waitlist) => !waitlist.dateRemoved && waitlist.activity.activityId === activity.activityId
    )?.activityWaitlistingId;

    onAction({
      enrolmentId,
      activityApplicationId,
      activityWaitlistingId,
      publishedRosteringId,
    })(action, { ...activity, status: activity.status });
  };

  const onSubmitAction = () => {
    const enrolmentId = volunteerData?.vm.volunteer?.activityEnrolments.find(
      (enrolment) => !enrolment.dateRemoved && enrolment.activity.activityId === selectedActivity.current?.activityId
    )?.activityEnrolmentId;
    const availabilityId = volunteerData?.vm.volunteer?.activityEnrolments
      .find((enrolment) => enrolment.activity.activityId === selectedActivity.current?.activityId)
      ?.availabilities?.find((availability) => availability.activityDate === selectedActivity.current?.activityDate)
      ?.activityAvailabilityId;
    return onSubmit({ enrolmentId, availabilityId });
  };

  const onClickCardElement = (myActivity: RosterTimelineItem) => {
    setQuery({ activityId: myActivity.activityId, activityDate: myActivity.activityDate });
  };

  const volunteers =
    filteredActivities
      .find((item) => item.activityId === selectedActivity.current?.activityId)
      ?.publishedRoster?.activeRosterings.map((item) => ({
        ...item.volunteer,
        sessionRosterings: item.sessionRosterings,
      })) || [];

  const handleSuccessCheckout = (sessionId: string) => {
    if (!sessionId) {
      onCloseDialog();
    }
    setCheckOutFinishedSessionId(sessionId);
    if (refetch) refetch();
    if (refetchListRosters) refetchListRosters();
  };

  const handleSuccessCheckin = () => {
    if (refetch) refetch();
    if (refetchListRosters) refetchListRosters();
  };

  const viewUpcomingRosters = useViewUpcomingRoster();

  const { refresh } = useOnboardingContext();

  React.useEffect(() => {
    viewUpcomingRosters.run({}).then(() => {
      refresh();
    });
  }, []);

  return (
    <>
      {activitiesWithLeaveCards.length ? (
        <List dense={isMobile}>
          {activitiesWithLeaveCards.slice(0, showSize).map((item) => {
            // Render leave period card
            if (item.type === 'leave') {
              return (
                <LeavePeriodCard
                  key={item.data.id}
                  type={item.data.leaveType}
                  date={item.data.date}
                  endDate={item.data.leaveType === 'start' ? item.data.endDate : undefined}
                  onEdit={handleEditLeave}
                />
              );
            }

            // Render activity card
            const myActivity = item.data;
            const nowDate = encodeDate(DateTime.local());
            const activeRosterings = myActivity.publishedRoster?.activeRosterings;
            const activeRostering = activeRosterings?.find(
              (rostering: any) => rostering.volunteer.volunteerId === volunteerId
            );
            const isCheckedIn = activeRostering?.volunteer.isCheckedIn;
            const volunteerCICOs = volunteerData?.vm.volunteer?.CICOs || [];
            const isAttended = activeRostering?.sessionRosterings.every(
              (sessionRostering: any) =>
                sessionRostering?.session &&
                volunteerCICOs.find(
                  (cico) =>
                    cico.checkIn &&
                    cico.checkOut &&
                    cico.sessionReport?.activityReport?.activityDate === myActivity.activityDate &&
                    cico.sessionReport?.activityReport?.activity?.activityId === myActivity.activityId &&
                    cico.sessionReport?.session?.sessionId === sessionRostering.session.sessionId
                )
            );
            const checkInTimeRule =
              unpackToTime(myActivity.startTime)
                .diffNow()
                .as('hours') <= 6;
            const checkedInActivityDate =
              activeRostering?.volunteer.checkedIn?.sessionReport.activityReport.activityDate;
            const checkedInActivityId =
              activeRostering?.volunteer.checkedIn?.sessionReport.activityReport.activity.activityId;
            const isAbleCheckIn =
              nowDate === myActivity.activityDate && checkInTimeRule && myActivity.status === 'rostered';
            const isAbleCheckOut =
              myActivity.status === 'rostered' &&
              checkedInActivityDate === myActivity.activityDate &&
              checkedInActivityId === myActivity.activityId &&
              !!isCheckedIn;

            const ableCICO =
              (myActivity.allowCICO || myActivity.allowCICO === null) &&
              (myActivity.program.allowCICO || myActivity.program.allowCICO === null) &&
              volunteerData?.vm.allowCICO &&
              !isAttended &&
              (isAbleCheckIn || isAbleCheckOut);

            const cicoConflict =
              isCheckedIn &&
              (checkedInActivityDate !== myActivity.activityDate || checkedInActivityId !== myActivity.activityId);

            // const ableCICO = () => {
            //   if (!isAttended && (isAbleCheckIn || isAbleCheckOut)) {
            //     if (myActivity.allowCICO === null) {
            //       if (myActivity.program.allowCICO === null) {
            //         return volunteerData?.vm.allowCICO;
            //       }
            //       return myActivity.program.allowCICO;
            //     }
            //     return myActivity.allowCICO;
            //   }
            //   return false;
            // };

            // Check if this activity falls within any leave period
            const hasLeaveConflict =
              myActivity.status === 'rostered' &&
              volunteerUnavailabilities.some((leave) => {
                const activityDateTime = DateTime.fromISO(myActivity.activityDate);
                const leaveStart = DateTime.fromISO(leave.startDate);
                const leaveEnd = DateTime.fromISO(leave.endDate);
                return activityDateTime >= leaveStart && activityDateTime <= leaveEnd;
              });

            return (
              <MyElementsMyActivityListItem
                rostering={getPublishedRostering(myActivity)}
                color={(theme.color.rosters.status as any)[myActivity.status]}
                key={`${myActivity.activityId} ${myActivity.activityDate}`}
                myActivity={displayingActivity(myActivity)}
                isSelected={
                  query.activityId === myActivity.activityId && query.activityDate === myActivity.activityDate
                }
                onClick={() => onClickCardElement(myActivity)}
                conflict={hasLeaveConflict}
                actionMenu={
                  ableCICO ? (
                    <MyActivityCICOActions
                      activity={myActivity}
                      onAction={(type) => onCICOAction(myActivity, type, isAbleCheckOut)}
                      cicoConflict={cicoConflict ?? false}
                      isCheckOut={isAbleCheckOut}
                    />
                  ) : (
                    <MyActivityStatusIndicator
                      actions={(() => {
                        const isVolunteer = userIdentityService.privLevel === 'volunteer';
                        const preventAvailability = configItems.vm.preventVolunteerAvailabilitySubmission === true;
                        const allowUnavailability = configItems.vm.allowUnavailabilityAndLeave === true;
                        if (isVolunteer && preventAvailability) {
                          if (allowUnavailability) {
                            return getRosterActions(myActivity.status, myActivity.hasOpenRoster).filter(
                              (a) => a === 'unavailable'
                            );
                          }
                          return [];
                        }
                        return getRosterActions(myActivity.status, myActivity.hasOpenRoster);
                      })()}
                      onAction={(action) => onActivityAction(action, myActivity)}
                      status={myActivity.status}
                    />
                  )
                }
              />
            );
          })}
        </List>
      ) : (
        <AlertCard variant='info' title={`There are no ${rosterWord}s to show`}>
          {volunteerData?.vm.volunteer?.activityEnrolments.length === 0 &&
          volunteerData?.vm.volunteer?.activityApplications.length === 0 &&
          volunteerData?.vm.volunteer?.activityWaitlistings.length === 0 ? (
            <Typography
              variant='subtitle2'
              style={{ fontSize: '14px', fontWeight: 400, color: lightTextColor, paddingTop: '5px' }}
            >
              It looks like you haven&apos;t joined {activityArticle} {activityWord} yet.{' '}
              <Link to={{ pathname: '/volunteering/activities' }}>
                <Typography
                  variant='body2'
                  display='inline'
                  style={{ textDecoration: 'underline', color: darkTextColor, fontWeight: 700 }}
                >
                  Click here
                </Typography>
              </Link>{' '}
              to view your organisation&apos;s {activityWordPlural}.
            </Typography>
          ) : (
            ''
          )}
        </AlertCard>
      )}
      <Box marginTop={isMobile ? '16px' : '32px'}>
        {!isLoading ? <ShowMoreButton onClick={handleSizeUpdate} /> : null}
      </Box>
      {selectedActivity.current && showDialog && (
        <ActivityAvailability
          open={showDialog}
          activityId={selectedActivity.current.activityId}
          onClose={onCloseDialog}
          selectedDate={selectedActivity.current.activityDate}
          onSubmit={onSubmitAction()}
          availabilityRestrictionDays={user.orgInfo.availabilityRestrictionDays}
        />
      )}
      {selectedActivity.current && showUpdateDialog && (
        <ActivityAvailability
          open={showUpdateDialog}
          activityId={selectedActivity.current.activityId}
          onClose={onCloseDialog}
          selectedDate={selectedActivity.current.activityDate}
          onSubmit={onSubmitAction()}
          availabilityRestrictionDays={user.orgInfo.availabilityRestrictionDays}
        />
      )}
      {showAlertDialog && (
        <AlertFullSessionDialog
          open={showAlertDialog}
          onClose={handleAlertClose}
          fullSessionIds={fullSessionIds}
          activityIdSubmited={activityIdSubmited}
          activityDatesSubmited={activityDatesSubmited}
        />
      )}

      {selectedActivity.current && showCheckInDialog && (
        <MyActivityCheckInDialog
          open={showCheckInDialog}
          type={CICODialogType}
          onClose={onCloseDialog}
          activity={selectedActivity.current}
          handleSuccess={handleSuccessCheckin}
          volunteers={volunteers}
        />
      )}

      {selectedActivity.current && showCheckOutDialog && (
        <MyActivityCheckOutDialog
          open={showCheckOutDialog}
          type={CICODialogType}
          onClose={() => setShowCheckOutDialog(false)}
          activity={selectedActivity.current}
          handleSuccess={handleSuccessCheckout}
        />
      )}
      {selectedActivity.current && checkOutFinishedSessionId && (
        <CheckOutFinishedNext
          open
          handleClose={() => setCheckOutFinishedSessionId(undefined)}
          activity={selectedActivity.current}
          checkOutFinishedSessionId={checkOutFinishedSessionId}
        />
      )}
    </>
  );
};
